import { FC, useEffect, useMemo } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import Link from 'next/link';
import appConfig from 'app-config';
import { RedDot } from 'src/components/RedDot';
import { useDMNotification } from 'src/hooks/useDMNotification';
import { usePrefetchHistoryData } from 'src/hooks/useHistoryQuery';
import { useHistoryTabRedDot } from 'src/hooks/useHistoryTabRedDot';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { TAB_CONFIGS, TabName } from 'src/hooks/useSelectedTab/types';
import { Routes } from 'src/router/route';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
export const BOTTOM_TAB_BAR_HEIGHT = '56px';
const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  /* PWA 모드에서 홈 인디케이터와 겹치지 않도록 하단 safe-area 패딩 추가 */
  padding-bottom: var(--sab);

  position: fixed;
  z-index: 10;
  /* PWA 모드에서 safe-area 높이를 포함한 전체 높이 계산 */
  height: calc(${BOTTOM_TAB_BAR_HEIGHT} + var(--sab));
  left: 0px;
  width: var(--100vw);
  bottom: 0px;
  background: ${({
  theme
}) => theme.color.surface1};
  ${({
  theme
}) => theme.screenSize.desktop} {
    display: none;
  }
`;
const StyledRedDot = styled(RedDot)`
  position: absolute;
  width: 8px;
  height: 8px;
  top: 0;
  inset-inline-end: -8px;
`;
const TabIcon = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  margin-bottom: 2px;
`;
const StyledLink = styled(Link, {
  shouldForwardProp: prop => isPropValid(prop)
})<{
  active: boolean;
}>`
  position: relative;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  cursor: pointer;
  font-size: 10px;
  color: ${({
  active,
  theme
}) => active ? theme.color.surface12 : theme.color.content8};
  ${({
  active
}) => active && `
    font-weight: 700;
  `}
  svg {
    fill: ${({
  active,
  theme
}) => active ? theme.color.contentDeep : theme.color.content8};
  }
`;
const BOTTOM_TAB_NAMES: TabName[] = ['mirror', 'history', 'message'];
const BOTTOM_TAB_CONFIGS = BOTTOM_TAB_NAMES.map(tabName => TAB_CONFIGS.find(({
  name
}) => name === tabName)).filter(tabConfig => tabConfig != null);
interface TabBehaviorConfig {
  onClick?: () => void;
  decoration?: React.ReactNode;
}
interface Props {
  className?: string;
}
const BottomTabBar: FC<Props> = ({
  className
}) => {
  const {
    selectedTab
  } = useSelectedTab();
  const hasHistoryRedDot = useHistoryTabRedDot();
  const hasDMNotification = useDMNotification();
  const prefetchHistoryData = usePrefetchHistoryData();
  useEffect(() => {
    if (BOTTOM_TAB_CONFIGS.find(({
      name
    }) => name === 'history')) {
      prefetchHistoryData();
    }
  }, [prefetchHistoryData]);
  const tabBehaviorMap: Partial<Record<TabName, TabBehaviorConfig>> = useMemo(() => ({
    history: hasHistoryRedDot ? {
      onClick: () => {
        pushEvent({
          eventName: EVENT_NAME.ANYWHERE__CLICK_RED_DOT,
          eventType: EVENT_TYPE.REDDOT,
          eventParams: {
            action_category: 'click',
            tab: 'anywhere',
            page: 'anywhere',
            referrer: 'history_tab',
            target: 'reddot',
            trigger: 'history_update'
          }
        });
      },
      decoration: <StyledRedDot type='default' referrer='history_tab' target='reddot' trigger='history_update' />
    } : {},
    message: hasDMNotification ? {
      decoration: <StyledRedDot type='default' referrer='message_tab' target='reddot' trigger='dm_unread' />
    } : {}
  }), [hasHistoryRedDot, hasDMNotification]);
  const tabs = BOTTOM_TAB_CONFIGS.filter(({
    name
  }) => {
    switch (name) {
      case 'message':
        return appConfig.features.dm.enabled;
      default:
        return true;
    }
  });
  return <Container className={className} data-testid='BottomTabBar' data-sentry-element="Container" data-sentry-component="BottomTabBar" data-sentry-source-file="index.tsx">
      {tabs.map(({
      icon: SVG,
      name,
      route,
      label
    }) => !!route && <StyledLink href={Routes[route]()} key={`tab-${name}`} active={selectedTab.name === name} data-testid={`tab-${name}`} onClick={tabBehaviorMap[name]?.onClick}>
              {SVG && <TabIcon>
                  <SVG />
                  {tabBehaviorMap[name]?.decoration}
                </TabIcon>}
              {label}
            </StyledLink>)}
    </Container>;
};
export default BottomTabBar;